import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { HelpPopover, NotPermitted, ReasonDialog, SPLIT_HOLD } from '@/design';
import { ApiError, type IEtagged } from '@/domains/api';
import { useAuth, useCan } from '@/domains/auth';
import type {
  IWorkItemAssignee,
  IWorkItemDecision,
  IWorkItemRow,
  RemovalReason,
  WorkItemDecisionCode
} from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import { formatDay } from '../../components/days';
import { refusalMessage } from '../../refusal';
import { escalateBlocked, lastLevelOf, useEscalationLevels } from './escalation';
import {
  REMOVAL_REASONS,
  decisionLabel,
  decisionsOf,
  movesOf,
  removalReasonOf
} from '../../work-items';

/**
 * The writes on one work item (SR-222, F9's R3, R4, R7, R8) — a decision, the assignee, the
 * follow-up day and a reopening — drawn in the item's pane, beside what raised it.
 *
 * **Which controls are drawn is `WORK_ITEM_MOVES`'** (`movesOf`, narrowed per item by
 * `decisionsOf`), the one mirror of the design's §6: a decision the item refuses is not drawn,
 * Reopen only on a reviewed item, the follow-up day only on a `pending_firm` one (survey-api
 * refuses it on any other state). survey-api still judges every move; a raced one comes back to
 * the pane.
 *
 * **What a write was told goes one of two ways.** A refusal the person can act on in place — a
 * problem at the rationale, the follow-up day or the assignee (400, 422) — is said beside the
 * control that made it, with what was typed kept. Everything that says the item is not what the
 * page read — 409 `invalid-transition` and `revision-conflict`, a 404, an answer that never
 * arrived — is the pane's (`onTrouble` answers `null` for it): said once at the top of the pane,
 * with the item read again, because no retry of the same control can fix it. So a dialog closes on
 * those, as on a success.
 *
 * **Concurrency is survey-api's** (R4): the assignee, the follow-up and a reopening carry the
 * `ETag` the pane read as `If-Match`, and the answer's `ETag` is adopted for the next write. A
 * decision carries none — survey-api's choice for a route that takes 200 items at once. An item
 * read without an `ETag` can take a decision but none of the three, said once rather than offered
 * and refused 428.
 *
 * Each control is held open against Esc while it holds something unsent or a write is out
 * (`SPLIT_HOLD`); a dialog is an overlay, which the split view's Esc already leaves alone.
 *
 * **A decision's rationale is required on Remove only** (CMP-10 AC1): Approve, Escalate and Need
 * additional info go without one, a blank one left out of the body (`decisionBody`), never sent as `""`.
 * Reopen's reason and a new task's note are required — survey-api requires both.
 */

/** survey-api's bound on a rationale, a reopening's reason and a task's note, said and never
 *  enforced. */
export const TEXT_MAX = 4_000;

/** A decision's body — the rationale, as `ReasonDialog` hands it back trimmed, left out where it is
 *  blank, the follow-up day only where there is one (`pending_firm`), and a removal's reason only
 *  where one was chosen (`remove_member`; absent, survey-api's rule per item). */
export function decisionBody(
  itemIds: string[],
  decision: WorkItemDecisionCode,
  rationale: string,
  followUpOn?: string,
  removalReason?: RemovalReason
): IWorkItemDecision {
  return {
    itemIds,
    decision,
    ...(rationale !== '' && { rationale }),
    ...(followUpOn !== undefined && { followUpOn }),
    ...(removalReason !== undefined && { removalReason })
  };
}

/** The body members survey-api's refusals point at, as the controls here name them. */
const TARGET_LABEL: Record<string, string> = {
  rationale: 'Rationale',
  reason: 'Reason',
  followUpOn: 'Follow up on',
  assigneeAccountId: 'Assigned to',
  itemIds: 'Item',
  subject: 'Subject',
  note: 'Note',
  disclosureId: 'Disclosure'
};

/**
 * A refusal the person can act on, in the words of the controls — each problem at the label of
 * the control it names, survey-api's reason verbatim — or survey-api's own sentence where it
 * names none. A 428 is this page's fault (it sent no revision), and is said as one.
 */
export function controlRefusal(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 428) {
      return (
        'The revision this page read is needed, and none was sent — a fault in this ' +
        'page. Reload it and try again.'
      );
    }
    const problems = error.problem?.problems ?? [];
    if (problems.length > 0) {
      return problems
        .map((problem) => `${TARGET_LABEL[problem.target] ?? problem.target}: ${problem.reason}`)
        .join('; ');
    }
  }
  return refusalMessage(error, 'The change was refused');
}

/** What the pane hands its writes. */
export interface IWorkItemWrites {
  row: IWorkItemRow;
  /** The revision to send as `If-Match` — null where survey-api sent none. */
  etag: Nullable<string>;
  /** Whom the item can be handed to; `null` while the list is out or could not be read. */
  assignees: Nullable<IWorkItemAssignee[]>;
  /** Why that list could not be read — `null` while it is out, or once it is. */
  assigneesError: Nullable<string>;
  /** A write landed: the row it answered (with its `ETag`, where the route sends one). */
  onLanded: (answer?: IEtagged<IWorkItemRow>) => void;
  /** A write failed: `null` where the pane said it (and reads the item again), else the sentence
   *  to say beside the control. */
  onTrouble: (error: unknown) => Nullable<string>;
}

/** What a decision, a reopening and an assignment are, in `NotPermitted`'s sentence — the three
 *  presses SR-228 split off the queue's `responses.annotate` (`work_items.decide`,
 *  `work_items.assign`), drawn disabled for a reader without them, in the pane and on a list's
 *  selection alike. */
export const NOT_PERMITTED_DECIDE = 'deciding work items';
export const NOT_PERMITTED_REOPEN = 'reopening work items';
export const NOT_PERMITTED_ASSIGN = 'assigning work items';
/** The assignee's `PATCH` is `responses.annotate` before survey-api asks `work_items.assign`. */
export const NOT_PERMITTED_CHANGE = 'changing work items';
export const NOT_PERMITTED_FOLLOW_UP = 'setting a work item’s follow-up day';
export const NOT_PERMITTED_RAISE = 'raising work items';

/** Today's UTC day plus one, `yyyy-MM-dd` — the follow-up control's `min`, for its shape only:
 *  survey-api's 400 at `followUpOn` is the judge (R5). */
export function tomorrowUtc(): string {
  return new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
}

/** The decisions the item's state takes, each a press confirmed with its rationale. */
export function DecisionButtons({ row, onLanded, onTrouble }: IWorkItemWrites) {
  const [open, setOpen] = React.useState<Nullable<WorkItemDecisionCode>>(null);
  const permitted = useCan()('work_items.decide');
  const reasonId = React.useId();
  const last = lastLevelOf(useEscalationLevels());
  const decisions = decisionsOf(row);
  if (decisions.length === 0) return null;
  const atLast = escalateBlocked([row], last);

  const decide = async (
    decision: WorkItemDecisionCode,
    rationale: string,
    followUpOn?: string,
    removalReason?: RemovalReason
  ) => {
    try {
      await workItemsApi.decideWorkItems(
        decisionBody([row.id], decision, rationale, followUpOn, removalReason)
      );
    } catch (error) {
      return onTrouble(error);
    }
    onLanded();
    return null;
  };

  return (
    <div className="grid gap-1">
      <div className="flex flex-wrap gap-2">
        <DecisionPresses
          decisions={decisions}
          onPress={setOpen}
          disabledFor={permitted ? undefined : reasonId}
          blocked={atLast ? { escalate: atLast } : undefined}
        />
      </div>
      {!permitted && <NotPermitted id={reasonId} what={NOT_PERMITTED_DECIDE} />}
      <DecisionDialog
        decision={open}
        about={row.subject}
        member={memberOf(row)}
        removalDefault={removalReasonOf(row)}
        escalatesTo={last ? (row.escalationLevel ?? 0) + 1 : undefined}
        onClose={() => setOpen(null)}
        onDecide={decide}
      />
    </div>
  );
}

/** The member a Remove just this advisor takes off, and the team or firm it leaves — by name, else
 *  by CRD. */
export function memberOf(row: IWorkItemRow): { name: string; team: string } {
  return {
    name: row.memberName ?? (row.crd ? `CRD ${row.crd}` : 'this advisor'),
    team: row.participant.displayName
  };
}

/** One button per decision offered — Approve (`keep`) the primary press, as it is the usual answer;
 *  Approved (`firm_approved`) on a firm move. Each carries its code as `data-decision`. */
export function DecisionPresses({
  decisions,
  onPress,
  disabledFor,
  blocked = {}
}: {
  decisions: readonly WorkItemDecisionCode[];
  onPress: (decision: WorkItemDecisionCode) => void;
  /** The id of the sentence saying why every press is off (`NotPermitted`); absent, they are on. */
  disabledFor?: string;
  /** A press held off for a reason of its own — Escalate at the last escalation level — said
   *  beside the presses. */
  blocked?: Partial<Record<WorkItemDecisionCode, string>>;
}) {
  const noteId = React.useId();
  const notes = decisions.flatMap((decision) => {
    const why = blocked[decision];
    return why ? [{ decision, why }] : [];
  });
  return (
    <>
      {decisions.map((decision) => {
        const why = blocked[decision];
        const described = [disabledFor, why && `${noteId}-${decision}`].filter(Boolean).join(' ');
        return (
          <Button
            key={decision}
            type="button"
            size="sm"
            variant={decision === 'keep' || decision === 'firm_approved' ? 'default' : 'outline'}
            disabled={disabledFor !== undefined || why !== undefined}
            data-decision={decision}
            aria-describedby={described || undefined}
            onClick={() => onPress(decision)}
          >
            {decisionLabel(decision)}
          </Button>
        );
      })}
      {notes.map(({ decision, why }) => (
        <span
          key={decision}
          id={`${noteId}-${decision}`}
          data-blocked={decision}
          className="self-center text-xs text-muted-foreground"
        >
          {why}
        </span>
      ))}
    </>
  );
}

/**
 * A decision's confirmation — the rationale, required on Remove only, and the follow-up day for
 * Need additional info — for one item in its pane, for every item ticked on a list (R7) and for the record
 * page's Escalate: one route, one dialog. A blank rationale comes back as `''`, for the caller to
 * leave out (`decisionBody`). `onDecide` answers `null` once the decision landed, or the sentence
 * to say inside the dialog.
 */
export function DecisionDialog({
  decision,
  about,
  member,
  removalDefault,
  escalatesTo,
  onClose,
  onDecide
}: {
  /** The decision pressed; `null` while the dialog is closed. */
  decision: Nullable<WorkItemDecisionCode>;
  /** What the decision is over, for the title — the item's subject, or "3 items". */
  about: string;
  /** One item's member, named by Remove just this advisor's title, press and note; absent over
   *  several. */
  member?: { name: string; team: string };
  /** The level an Escalate takes one item to, said in its dialog — absent without a hierarchy, or
   *  over several items. survey-api picks the person. */
  escalatesTo?: number;
  /** The removal reason preselected — the items' shared rule (`removalReasonOf`); null where they
   *  differ, which offers "Each item's own", preselected, sending none. */
  removalDefault?: Nullable<RemovalReason>;
  onClose: () => void;
  onDecide: (
    decision: WorkItemDecisionCode,
    rationale: string,
    followUpOn?: string,
    removalReason?: RemovalReason
  ) => Promise<Nullable<string>>;
}) {
  const [followUp, setFollowUp] = React.useState('');
  const [removal, setRemoval] = React.useState<RemovalReason | 'own'>(removalDefault ?? 'own');
  // Each opening starts afresh: a day and a reason belong to the press they were chosen for. Keyed
  // by the opening alone, so a re-read of the rows under an open dialog keeps the reason chosen.
  const shut = decision === null;
  React.useEffect(() => {
    if (shut) setFollowUp('');
    else setRemoval(removalDefault ?? 'own');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shut]);

  return (
    <ReasonDialog
      open={decision !== null}
      onOpenChange={(next) => !next && onClose()}
      title={
        decision === null
          ? ''
          : decision === 'remove_member' && member
            ? `Remove ${member.name} from ${member.team}?`
            : `${decisionLabel(decision)}: ${about}`
      }
      description={decision === null ? '' : <DecisionSays decision={decision} />}
      reasonLabel="Rationale"
      required={decision === 'remove'}
      max={TEXT_MAX}
      confirmLabel={
        decision === null
          ? 'Decide'
          : decision === 'remove_member' && member
            ? `Remove ${member.name}`
            : decisionLabel(decision)
      }
      incomplete={decision === 'pending_firm' && followUp === ''}
      onConfirm={(rationale) =>
        onDecide(
          decision as WorkItemDecisionCode,
          rationale,
          decision === 'pending_firm' ? followUp : undefined,
          decision === 'remove_member' && removal !== 'own' ? removal : undefined
        )
      }
    >
      {decision === 'pending_firm' && <FollowUpField value={followUp} onChange={setFollowUp} />}
      {decision === 'escalate' && escalatesTo !== undefined && (
        <p data-escalates-to={escalatesTo} className="text-sm">
          Goes to level {escalatesTo}
        </p>
      )}
      {decision === 'remove_member' && (
        <RemovalReasonField
          value={removal}
          offerOwn={removalDefault == null}
          member={member?.name}
          onChange={setRemoval}
        />
      )}
    </ReasonDialog>
  );
}

/** Remove just this advisor's reason — three radios, a fourth "Each item's own" over items whose
 *  rule differs — and, for one item, the note it writes on the team. */
function RemovalReasonField({
  value,
  offerOwn,
  member,
  onChange
}: {
  value: RemovalReason | 'own';
  offerOwn: boolean;
  member?: string;
  onChange: (next: RemovalReason | 'own') => void;
}) {
  const name = React.useId();
  const chosen = REMOVAL_REASONS.find((reason) => reason.code === value);
  const options: { code: RemovalReason | 'own'; label: string }[] = [
    ...REMOVAL_REASONS,
    ...(offerOwn ? [{ code: 'own' as const, label: 'Each item’s own' }] : [])
  ];
  return (
    <fieldset className="grid gap-1.5 text-sm" data-removal-reason={value}>
      <legend className="mb-1 text-muted-foreground">Reason</legend>
      {options.map((option) => (
        <label key={option.code} className="flex w-fit items-center gap-2">
          <input
            type="radio"
            name={name}
            required
            className="accent-[var(--color-sapphire)]"
            checked={value === option.code}
            onChange={() => onChange(option.code)}
          />
          {option.label}
        </label>
      ))}
      {member && chosen && (
        <span data-removal-note="" className="text-xs text-muted-foreground">
          Note: REMOVE {member} due to {chosen.note}
        </span>
      )}
    </fieldset>
  );
}

/** What each decision does, said in its dialog before the press — survey-api's moves (§6). Said
 *  of "the item" over a list's selection too: the decision is the same on each. What follows from
 *  it beyond the item is the "?"'s (`DECISION_HELP`), not a line of the dialog. */
const DECISION_SAYS: Record<WorkItemDecisionCode, string> = {
  keep: 'The item is reviewed and closed; the participant stays eligible.',
  remove:
    'The item is reviewed and closed, and the participant is marked ineligible on this survey.',
  escalate: 'The item stays unclosed, escalated for a second look.',
  pending_firm: 'The item waits for more information until the day you choose, then comes back.',
  remove_member:
    'The item is reviewed and closed; the team keeps its standing, and the note names this advisor.',
  left_firm: 'The item is reviewed and closed: the person has left the firm.',
  firm_approved: 'The item is reviewed and closed: the move to the new firm is approved.'
};

const DECISION_HELP: Partial<Record<WorkItemDecisionCode, React.ReactNode>> = {
  remove: (
    <p>
      Remove also sets the participant’s Quality Rating on this survey to “U4 issues” and Travel
      to “NO” — where the survey has those fields.
    </p>
  ),
  remove_member: (
    <>
      <p>The team keeps its standing: its Quality Rating and Travel do not change.</p>
      <p>
        A note naming the advisor goes to the team’s staff notes, and to Team Member Removal in its
        U4 section where the survey has that field.
      </p>
    </>
  ),
  left_firm: (
    <p>
      Left firm sets the participant’s Quality Rating on this survey to “Left Firm”, and Travel to
      “NO”.
    </p>
  ),
  firm_approved: (
    <p>
      The participant’s standing does not change; their firm name becomes the newly listed firm.
    </p>
  ),
  escalate: (
    <>
      <p>
        With escalation levels set, the item goes to the next level, and to that level’s member
        with the fewest open items. Without them, it stays with its assignee.
      </p>
      <p>
        The rationale goes to the item’s History, and shows in the queue’s Latest note column.
      </p>
    </>
  )
};

/** A decision's sentence, and its "?" where it has one. */
function DecisionSays({ decision }: { decision: WorkItemDecisionCode }) {
  const help = DECISION_HELP[decision];
  return (
    <>
      {DECISION_SAYS[decision]}
      {help && (
        <HelpPopover label={`About ${decisionLabel(decision)}`} className="ml-1 align-middle">
          {help}
        </HelpPopover>
      )}
    </>
  );
}

/** The day a `pending_firm` item is followed up on — in the decision's dialog and in the pane.
 *  A date input hands over a half-typed day as `''`, like an emptied one; `badInput` is what tells
 *  them apart (the `AdminInlineField` lesson), and `onChange` says it, so `''` never stands for a
 *  day the person is still typing. */
function FollowUpField({
  value,
  onChange,
  disabled,
  describedBy
}: {
  value: string;
  onChange: (next: string, partial: boolean) => void;
  disabled?: boolean;
  /** The id of a `NotPermitted` sentence, where the field is drawn off for want of authority. */
  describedBy?: string;
}) {
  const id = React.useId();
  return (
    <div className="grid gap-1 text-sm">
      <label htmlFor={id} className="text-muted-foreground">
        Follow up on
      </label>
      <Input
        id={id}
        type="date"
        min={tomorrowUtc()}
        value={value}
        disabled={disabled}
        aria-describedby={describedBy}
        className="max-w-48"
        onChange={(event) => onChange(event.target.value, event.target.validity.badInput)}
      />
    </div>
  );
}

/** Reopen, on a reviewed item alone: back to Outstanding, with a reason. */
export function ReopenButton({ row, etag, onLanded, onTrouble }: IWorkItemWrites) {
  const [open, setOpen] = React.useState(false);
  const permitted = useCan()('work_items.decide');
  const reasonId = React.useId();
  if (!movesOf(row.state).reopen || etag === null) return null;

  const reopen = async (reason: string) => {
    let answer: IEtagged<IWorkItemRow>;
    try {
      answer = await workItemsApi.reopenWorkItem(row.id, etag, reason);
    } catch (error) {
      return onTrouble(error);
    }
    onLanded(answer);
    return null;
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={!permitted}
        aria-describedby={permitted ? undefined : reasonId}
        onClick={() => setOpen(true)}
      >
        Reopen
      </Button>
      {!permitted && <NotPermitted id={reasonId} what={NOT_PERMITTED_REOPEN} />}
      <ReasonDialog
        open={open}
        onOpenChange={setOpen}
        title={`Reopen: ${row.subject}`}
        description="The item goes back to Outstanding, to be decided again."
        required
        max={TEXT_MAX}
        confirmLabel="Reopen"
        onConfirm={reopen}
      />
    </>
  );
}

/** survey-api's `null` assignee, as a value the kit's select can hold. */
const UNASSIGNED = '__unassigned__';

/**
 * Whom the item is assigned to: one choice, written as it is made (`PATCH` with `If-Match`), and
 * "Assign to me" for the viewer's own account. A choice is the whole edit, so there is no pencil
 * and no Save — the answer replaces the choice, and a refusal is said under it with the item's
 * assignee shown as last read. An assignee the list does not hold (an account since deactivated)
 * is still drawn, by address, as the current choice.
 */
export function AssigneeControl({
  row,
  etag,
  assignees,
  assigneesError,
  onLanded,
  onTrouble
}: IWorkItemWrites) {
  const { user } = useAuth();
  const can = useCan();
  // The route is `responses.annotate`; naming an assignee is `work_items.assign` on top of it,
  // checked in survey-api's handler — so both, and the sentence names the one missing.
  const missing = !can('responses.annotate')
    ? NOT_PERMITTED_CHANGE
    : !can('work_items.assign')
      ? NOT_PERMITTED_ASSIGN
      : null;
  const permitted = missing === null;
  const [saving, setSaving] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const id = React.useId();
  const reasonId = React.useId();
  const current = row.assignee?.accountId ?? UNASSIGNED;
  const choices = [
    ...(assignees ?? []).map(({ accountId, email }) => ({ accountId, email })),
    ...(row.assignee && !assignees?.some((a) => a.accountId === row.assignee?.accountId)
      ? [row.assignee]
      : [])
  ];

  const assign = async (accountId: Nullable<string>) => {
    if (etag === null || accountId === (row.assignee?.accountId ?? null)) return;
    setSaving(true);
    setRefusal(null);
    try {
      onLanded(await workItemsApi.patchWorkItem(row.id, etag, { assigneeAccountId: accountId }));
    } catch (error) {
      setRefusal(onTrouble(error));
    } finally {
      setSaving(false);
    }
  };

  const mine = user !== null && row.assignee?.accountId !== user.accountId;
  return (
    <div className="grid gap-1 text-sm" {...(saving && SPLIT_HOLD)}>
      <label htmlFor={id} className="text-muted-foreground">
        Assigned to
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={current}
          disabled={saving || etag === null || !permitted}
          onValueChange={(next) => void assign(next === UNASSIGNED ? null : next)}
        >
          <SelectTrigger
            id={id}
            className="w-72 max-w-full"
            aria-describedby={permitted ? undefined : reasonId}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
            {choices.map((choice) => (
              <SelectItem key={choice.accountId} value={choice.accountId}>
                {choice.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {mine && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={saving || etag === null || !permitted}
            aria-describedby={permitted ? undefined : reasonId}
            onClick={() => void assign(user.accountId)}
          >
            Assign to me
          </Button>
        )}
        {saving && <span className="text-muted-foreground">Saving…</span>}
      </div>
      {missing !== null && <NotPermitted id={reasonId} what={missing} />}
      {assigneesError && (
        <span className="text-xs text-muted-foreground">
          The list of staff could not be read ({assigneesError}) — only Unassigned and you can be
          chosen.
        </span>
      )}
      {refusal && (
        <p role="alert" className="text-destructive">
          {refusal}
        </p>
      )}
    </div>
  );
}

/**
 * The day a parked item comes back — drawn on `pending_firm` alone, the one state survey-api
 * takes it on. A day typed and not yet saved holds the record open against Esc — while it can
 * still be saved: a permission taken under the open page leaves a draft nothing can send, and a
 * hold on it would keep Esc dead. Enter or Save sends it, and an emptied box clears it (`null`).
 */
export function FollowUpControl({ row, etag, onLanded, onTrouble }: IWorkItemWrites) {
  const [draft, setDraft] = React.useState(row.followUpOn ?? '');
  // A half-typed day: held, never sent — it reads `''`, which would clear the day stored.
  const [partial, setPartial] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  // The day is a `PATCH`, `responses.annotate` (SR-228) — not the decision's split.
  const permitted = useCan()('responses.annotate');
  const reasonId = React.useId();
  React.useEffect(() => {
    setDraft(row.followUpOn ?? '');
    setPartial(false);
  }, [row.followUpOn]);
  if (row.state !== 'pending_firm' || etag === null) return null;

  const dirty = partial || draft !== (row.followUpOn ?? '');
  const save = async () => {
    if (!dirty || saving) return;
    if (partial) {
      setRefusal('Enter a full date — nothing was sent.');
      return;
    }
    setSaving(true);
    setRefusal(null);
    try {
      onLanded(
        await workItemsApi.patchWorkItem(row.id, etag, { followUpOn: draft === '' ? null : draft })
      );
    } catch (error) {
      setRefusal(onTrouble(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="grid gap-1 text-sm"
      {...((dirty || saving) && permitted && SPLIT_HOLD)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
          event.preventDefault();
          void save();
        }
      }}
    >
      <FollowUpField
        value={draft}
        onChange={(next, half) => {
          setDraft(next);
          setPartial(half);
        }}
        disabled={saving || !permitted}
        describedBy={permitted ? undefined : reasonId}
      />
      {!permitted && <NotPermitted id={reasonId} what={NOT_PERMITTED_FOLLOW_UP} />}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!dirty || saving || !permitted}
          aria-describedby={permitted ? undefined : reasonId}
          onClick={() => void save()}
        >
          {saving ? 'Saving…' : 'Set follow-up'}
        </Button>
        {row.followUpOn && !dirty && (
          <span className="text-muted-foreground">Comes back on {formatDay(row.followUpOn)}.</span>
        )}
      </div>
      {refusal && (
        <p role="alert" className="text-destructive">
          {refusal}
        </p>
      )}
    </div>
  );
}
